import type React from 'react';
import type { ComponentProps, ComponentPropsWithoutRef, Ref } from 'react';
import { useCallback, useId, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Observable } from 'rxjs';
import { cn } from '../../common/functions/cn';
import { combineRefs } from '../../common/functions/combine-refs';
import useTestTag from '../../common/hooks/use-test-tag';
import { ButtonV2, type ButtonV2Props } from '../button-v2/button-v2';
import type { ModalDialogButtonSetting } from '../common-interfaces';
import OdinIcon from '../odin-icon/odin-icon.component';
import { Spinner } from '../spinner/spinner';

interface DialogResult {
  buttonName: string | undefined;
}

export interface DialogRef {
  show: () => void;
  close: () => void;
  subscribeToDialogResult?: () => Observable<DialogResult> | undefined;
}

export interface DialogProps extends Omit<ComponentPropsWithoutRef<'dialog'>, 'onClose'> {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType?: 'button' | 'submit';
  description?: string;
  fullScreen?: boolean;
  /** Names the dialog via `aria-labelledby`, and reserves the row the close icon floats over. */
  header: string;
  loading?: boolean;
  ref?: Ref<DialogRef>;
  showCloseIcon?: boolean;
  visible?: boolean;
  onClose?: (buttonName?: string) => void;
}

const DialogCloseButton = ({ className, ...props }: ButtonV2Props) => {
  const { t } = useTranslation();

  return (
    <ButtonV2
      variant="ghost"
      size="icon-sm"
      className={cn('absolute top-2 right-2', className)}
      data-testid="modal-close-icon"
      {...props}
    >
      <OdinIcon icon="X" />
      <span className="sr-only">{t('close')}</span>
    </ButtonV2>
  );
};

interface DialogTextProps {
  id: string;
  children: string;
}

const DialogTitle = ({ id, children }: DialogTextProps) => (
  <h2 data-slot="dialog-title" id={id} className="font-semibold text-base leading-none">
    {children}
  </h2>
);

const DialogDescription = ({ id, children }: DialogTextProps) => (
  <p data-slot="dialog-description" id={id} className="text-muted-foreground text-sm">
    {children}
  </p>
);

const DialogHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
);

const DialogScrollArea = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="dialog-scroll-area"
    className={cn('relative -mx-4 -my-2 min-h-0 flex-1 overflow-y-auto px-4 py-2', className)}
    {...props}
  />
);

interface DialogFooterProps {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType: 'button' | 'submit';
  onClose: (buttonName?: string) => void;
}

const DialogFooter = ({
  buttons,
  buttonType,
  onClose,
  className,
  ...props
}: DialogFooterProps & Omit<ComponentProps<'div'>, 'onClose'>) => {
  if (!buttons) return null;

  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '-mx-4 -mb-4 flex shrink-0 flex-col-reverse gap-2 rounded-b-md border-t border-solid bg-popover p-4 sm:flex-row sm:items-center sm:justify-end',
        className
      )}
      {...props}
    >
      {buttons.map(({ text, classNames, closeModalOnClick, onClick, ...buttonProps }) => (
        <ButtonV2
          key={buttonProps.name}
          {...buttonProps}
          type={buttonType}
          className={classNames}
          onClick={() => {
            onClick?.();
            if (closeModalOnClick) {
              onClose(buttonProps.name);
            }
          }}
        >
          {text}
        </ButtonV2>
      ))}
    </div>
  );
};

function Dialog({
  buttons,
  buttonType = 'button',
  children,
  className,
  description,
  fullScreen = false,
  header,
  id,
  loading = false,
  ref,
  showCloseIcon = true,
  visible = false,
  onClose,
  ...props
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tagCreator = useTestTag('modal');
  const onCloseCalledRef = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const hasContent = children !== undefined && children !== null && children !== false;

  // Sync native dialog open state with visible prop
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only make DOM calls when current state doesn't match target state.
    // This prevents race conditions from rapid visible prop changes because
    // we check the actual dialog.open state right before each DOM call.
    if (visible && !dialog.open) {
      dialog.showModal();
      // Reset the flag only during open transition (closed → open).
      // This allows onClose to be called when the dialog actually closes.
      onCloseCalledRef.current = false;
    } else if (!visible && dialog.open) {
      dialog.close();
    }
  }, [visible]);

  const show = () => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  };

  const close = useCallback(
    (buttonName?: string) => {
      const dialog = dialogRef.current;
      if (dialog?.open) {
        dialog.close();
        // Call onClose immediately to ensure parent state is updated without waiting for the native 'close' event.
        // Set the flag AFTER calling onClose to prevent handleNativeClose from calling it again.
        // This handles cases where dialog.close() triggers the native 'close' event synchronously.
        onClose?.(buttonName);
        onCloseCalledRef.current = true;
      } else {
        // If already closed, just notify parent (only if not already called)
        if (!onCloseCalledRef.current) {
          onCloseCalledRef.current = true;
          onClose?.(buttonName);
        }
      }
    },
    [onClose]
  );

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  const handleNativeClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // Without this, closing a nested dialog will also close any parent dialog.
      event.stopPropagation();

      // This fires when the dialog is closed by ANY means (ESC, dialog.close()).
      // A close that carries a button name has already notified the parent from close(), so anything
      // reaching here is a dismissal and reports no button name.
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        onClose?.();
      }
    },
    [onClose]
  );

  const dialogStyles = cn(
    'm-auto flex flex-col gap-4 p-4',
    // The UA caps a dialog 6px tighter than that, so max-width is set rather than left to it.
    'max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-[min(24rem,100%-2rem)]',
    fullScreen && 'h-[calc(100%-2rem)] max-w-[calc(100%-2rem)]',
    'overflow-auto rounded-md bg-popover text-popover-foreground text-sm outline-hidden ring-1 ring-border/80 [&:not([open])]:pointer-events-none',
    // Open and close transition
    'scale-95 opacity-0 transition-[opacity,scale,overlay,display] transition-discrete duration-100 ease-out motion-reduce:transition-none',
    'open:scale-100 starting:open:scale-95 open:opacity-100 starting:open:opacity-0',
    // Backdrop transition
    'backdrop:opacity-0 backdrop:transition-[opacity,overlay,display] backdrop:transition-discrete backdrop:duration-100 backdrop:ease-out motion-reduce:backdrop:transition-none',
    'backdrop:bg-black/60 open:backdrop:opacity-100 starting:open:backdrop:opacity-0',
    className
  );

  return createPortal(
    <dialog
      data-slot="dialog"
      ref={combineRefs(dialogRef, tagCreator('container'))}
      id={id}
      className={dialogStyles}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClose={handleNativeClose}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 z-10 grid place-content-center rounded-md bg-popover/40">
          <Spinner className="size-6" />
        </div>
      )}
      <DialogHeader className={cn(showCloseIcon && 'pr-8')}>
        <DialogTitle id={titleId}>{header}</DialogTitle>
        {description && <DialogDescription id={descriptionId}>{description}</DialogDescription>}
      </DialogHeader>
      {hasContent && <DialogScrollArea>{children}</DialogScrollArea>}
      <DialogFooter buttons={buttons} buttonType={buttonType} onClose={close} />
      {showCloseIcon && <DialogCloseButton onClick={() => close('close-icon')} />}
    </dialog>,
    document.body
  );
}

export default Dialog;
